import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { useLanguage } from "@/shared/hooks/use-language";
import { useToast } from "@/shared/hooks/use-toast";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import { labelOr } from "@/shared/lib/label";
import { instrumentTypeLabelKey } from "@/shared/lib/instrument-type";
import type { ScopeContext } from "@/features/auth";
import {
  createInstrumentRequest,
  getInstrumentById,
  getInstrumentRequest,
  getInstruments,
  getInstrumentRequestFailureReasons,
  getResolvedInstrumentReference,
  previewInstruments,
  type Instrument,
  type InstrumentCandidate,
} from "@/features/portfolio/api/instrument";
import { accountScopeOf } from "@/features/portfolio/api/portfolio";

interface InstrumentPickerProps {
  value: Instrument | null;
  onChange: (instrument: Instrument | null) => void;
  error?: string;
}

const RESOLVE_POLL_MS = 2000;
const RESOLVE_TIMEOUT_MS = 90000;
const LOCAL_MATCH_LIMIT = 20;

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const ISIN_PATTERN = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/i;
const FIGI_PATTERN = /^BBG[A-Z0-9]{9}$/i;

const ASSET_CLASSES = ["equity", "debt", "currency", "crypto"] as const;
const ENABLED_ASSET_CLASSES: readonly string[] = ["equity"];

interface ExchangeInfo {
  displayName: string;
  country: string;
}

const EXCHANGE_INFO_BY_VALUE: Record<string, ExchangeInfo> = {
  NYSE: { displayName: "New York Stock Exchange", country: "US" },
  NASDAQ: { displayName: "Nasdaq Stock Market", country: "US" },
  AMEX: { displayName: "NYSE American", country: "US" },
  B3: { displayName: "Brasil, Bolsa, Balcão", country: "BR" },
  LSE: { displayName: "London Stock Exchange", country: "GB" },
  TSX: { displayName: "Toronto Stock Exchange", country: "CA" },
  TSXV: { displayName: "TSX Venture Exchange", country: "CA" },
  TSE: { displayName: "Tokyo Stock Exchange", country: "JP" },
  XETRA: { displayName: "Deutsche Börse Xetra", country: "DE" },
  HKEX: { displayName: "Hong Kong Stock Exchange", country: "HK" },
  ASX: { displayName: "Australian Securities Exchange", country: "AU" },
  NSE: { displayName: "National Stock Exchange of India", country: "IN" },
  BYMA: { displayName: "Bolsas y Mercados Argentinos", country: "AR" },
  "EURONEXT-AMSTERDAM": { displayName: "Euronext Amsterdam", country: "NL" },
  "EURONEXT-PARIS": { displayName: "Euronext Paris", country: "FR" },
  "EURONEXT-BRUSSELS": { displayName: "Euronext Brussels", country: "BE" },
  "EURONEXT-LISBON": { displayName: "Euronext Lisbon", country: "PT" },
  "EURONEXT-DUBLIN": { displayName: "Euronext Dublin", country: "IE" },
  "EURONEXT-OSLO": { displayName: "Euronext Oslo", country: "NO" },
  "EURONEXT-MILAN": { displayName: "Euronext Milan", country: "IT" },
};

const EXCHANGE_COUNTRIES = [
  ...new Set(Object.values(EXCHANGE_INFO_BY_VALUE).map((info) => info.country)),
];

const exchangeDisplayName = (value: string): string =>
  EXCHANGE_INFO_BY_VALUE[value.toUpperCase()]?.displayName ?? value;

const regionNames = (language: string): Intl.DisplayNames | null => {
  try {
    return new Intl.DisplayNames([language], { type: "region" });
  } catch {
    return null;
  }
};

const nameCollator = (language: string): Intl.Collator => {
  try {
    return new Intl.Collator(language);
  } catch {
    return new Intl.Collator();
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const identifierOf = (query: string): { isin?: string; figi?: string } | null => {
  const candidate = query.trim().toUpperCase();
  if (FIGI_PATTERN.test(candidate)) return { figi: candidate };
  if (ISIN_PATTERN.test(candidate)) return { isin: candidate };
  return null;
};

export const InstrumentPicker = ({ value, onChange, error }: InstrumentPickerProps) => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext: ScopeContext | null = sessionId
    ? ScopeContextStorage.getScopeContext(sessionId)
    : null;
  const accountScope = scopeContext ? accountScopeOf(scopeContext) : null;

  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [isInstrumentListTruncated, setIsInstrumentListTruncated] = useState(false);
  const [isInstrumentListIncomplete, setIsInstrumentListIncomplete] = useState(false);
  const [isLoadingInstruments, setIsLoadingInstruments] = useState(true);
  const [query, setQuery] = useState("");
  const [assetClass, setAssetClass] = useState<string>("equity");
  const [country, setCountry] = useState("");
  const [candidates, setCandidates] = useState<InstrumentCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const isMounted = useRef(true);
  const searchGeneration = useRef(0);
  const hasStartedInstrumentLoad = useRef(false);

  const discardCandidates = () => {
    searchGeneration.current += 1;
    setCandidates([]);
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionId || !scopeContext) {
      setIsLoadingInstruments(false);
      return;
    }
    // React StrictMode mounts, cleans up, and remounts this effect once in development.
    // A boolean ref (rather than the returned cleanup) survives that remount, so the second
    // pass sees the load already started and never issues a second full-catalog drain.
    if (hasStartedInstrumentLoad.current) return;
    hasStartedInstrumentLoad.current = true;
    getInstruments(sessionId, scopeContext, { isCancelled: () => !isMounted.current })
      .then((response) => {
        if (!isMounted.current) return;
        setInstruments(response.instruments || []);
        setIsInstrumentListTruncated(response.isTruncated);
        setIsInstrumentListIncomplete(response.isIncomplete);
      })
      .catch(() => {
        if (isMounted.current) {
          setInstruments([]);
          setIsInstrumentListIncomplete(true);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsLoadingInstruments(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const matches = normalizedQuery
    ? instruments.filter(
        (instrument) =>
          (instrument.symbol || "").toLowerCase().includes(normalizedQuery) ||
          (instrument.name || "").toLowerCase().includes(normalizedQuery) ||
          (instrument.isin || "").toLowerCase() === normalizedQuery,
      )
    : instruments;

  const identifier = identifierOf(query);

  const isInstrumentListIncompleteOrTruncated = isInstrumentListTruncated || isInstrumentListIncomplete;

  const emptyInstrumentMessageKey = (): string => {
    if (!isInstrumentListIncompleteOrTruncated) {
      return "portfolio.noInstrumentMatch";
    }
    return accountScope && !identifier
      ? "portfolio.noLocalInstrumentMatchExternal"
      : "portfolio.noLocalInstrumentMatch";
  };

  const countryOptions = useMemo(() => {
    const displayNames = regionNames(language);
    const collator = nameCollator(language);
    return EXCHANGE_COUNTRIES.map((code) => ({ code, name: displayNames?.of(code) ?? code }))
      .sort((first, second) => collator.compare(first.name, second.name));
  }, [language]);

  const handleSearchExternal = async () => {
    if (!sessionId || !accountScope || !query.trim()) return;
    searchGeneration.current += 1;
    const generation = searchGeneration.current;
    const isCurrentSearch = () => isMounted.current && searchGeneration.current === generation;
    setIsSearching(true);
    try {
      const found = await previewInstruments(sessionId, accountScope, query.trim(), country || undefined);
      if (!isCurrentSearch()) return;
      setCandidates(found);
      if (found.length === 0) {
        toast({ title: t("portfolio.noExternalMatch") });
      }
    } catch (err) {
      if (isCurrentSearch()) setCandidates([]);
      toast({
        title: err instanceof Error ? err.message : t("portfolio.instrumentRequestFailed"),
        variant: "destructive",
      });
    } finally {
      if (isMounted.current) setIsSearching(false);
    }
  };

  const waitForResolvedInstrument = async (requestId: string): Promise<Instrument | null> => {
    const deadline = Date.now() + RESOLVE_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await wait(RESOLVE_POLL_MS);
      if (!isMounted.current || !sessionId || !scopeContext || !accountScope) return null;

      const request = await getInstrumentRequest(sessionId, accountScope, requestId);
      if (request?.status === "failed") {
        const reasons = await getInstrumentRequestFailureReasons(sessionId, accountScope, requestId);
        throw new Error(
          reasons.length > 0
            ? `${t("portfolio.instrumentRequestFailed")}: ${reasons.join(", ")}`
            : t("portfolio.instrumentRequestFailed"),
        );
      }
      if (request?.status !== "success") continue;

      const reference = await getResolvedInstrumentReference(sessionId, accountScope, requestId);
      if (!reference) continue;
      return getInstrumentById(
        sessionId, scopeContext, reference.instrument_id, reference.instrument_type,
      );
    }
    return null;
  };

  const handleRequestCandidate = async (candidate: InstrumentCandidate) =>
    requestInstrument(candidate.figi ? { figi: candidate.figi } : { isin: candidate.isin });

  const requestInstrument = async (params: { isin?: string; figi?: string }) => {
    if (!sessionId || !accountScope) return;
    setIsRequesting(true);
    try {
      const request = await createInstrumentRequest(sessionId, accountScope, params);
      toast({ title: t("portfolio.instrumentRequested") });
      const instrument = await waitForResolvedInstrument(request.id);
      if (!isMounted.current) return;
      if (!instrument) {
        toast({ title: t("portfolio.instrumentStillResolving"), variant: "destructive" });
        return;
      }
      discardCandidates();
      setQuery("");
      setInstruments((previous) =>
        previous.some((existing) => existing.id === instrument.id) ? previous : [...previous, instrument],
      );
      onChange(instrument);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t("portfolio.instrumentRequestFailed"),
        variant: "destructive",
      });
    } finally {
      if (isMounted.current) setIsRequesting(false);
    }
  };

  if (value) {
    return (
      <div
        className="flex items-center justify-between rounded-md border border-input px-3 py-2"
        data-testid="selected-instrument"
      >
        <div>
          <p className="text-sm font-medium" data-testid="selected-instrument-symbol">{value.symbol}</p>
          <p className="text-xs text-muted-foreground">{value.name}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          {t("portfolio.changeInstrument")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="instrument-picker">
      <Input
        id="instrument-search"
        placeholder={t("portfolio.searchInstrument")}
        aria-label={t("portfolio.searchInstrument")}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          discardCandidates();
        }}
        className={error ? "border-destructive" : undefined}
        data-testid="instrument-search"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex-1 space-y-1">
          <label className="text-sm font-medium" htmlFor="preview-asset-class">
            {t("portfolio.assetClass")}
          </label>
          <select
            id="preview-asset-class"
            className={SELECT_CLASS}
            value={assetClass}
            onChange={(event) => setAssetClass(event.target.value)}
            data-testid="preview-asset-class"
          >
            {ASSET_CLASSES.map((option) => (
              <option key={option} value={option} disabled={!ENABLED_ASSET_CLASSES.includes(option)}>
                {ENABLED_ASSET_CLASSES.includes(option)
                  ? labelOr(t, instrumentTypeLabelKey(option), option)
                  : `${labelOr(t, instrumentTypeLabelKey(option), option)} ${t("portfolio.comingSoon")}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-sm font-medium" htmlFor="preview-country-filter">
            {t("portfolio.countryFilter")}
          </label>
          <select
            id="preview-country-filter"
            className={SELECT_CLASS}
            value={country}
            onChange={(event) => {
              setCountry(event.target.value);
              discardCandidates();
            }}
            data-testid="preview-country-filter"
          >
            <option value="">{t("portfolio.allCountries")}</option>
            {countryOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {matches.length > LOCAL_MATCH_LIMIT && (
        <p className="text-xs text-muted-foreground" data-testid="instrument-list-count">
          {t("portfolio.localResultCount")
            .replace("{shown}", String(LOCAL_MATCH_LIMIT))
            .replace("{total}", String(matches.length))}
        </p>
      )}
      <div className="max-h-40 overflow-y-auto rounded-md border border-input divide-y" data-testid="instrument-list">
        {isLoadingInstruments && (
          <div className="p-3 space-y-2" data-testid="instrument-list-loading">
            <div className="skeleton-shimmer h-4 w-2/3 rounded bg-muted" />
            <div className="skeleton-shimmer h-4 w-1/2 rounded bg-muted" />
            <div className="skeleton-shimmer h-4 w-3/5 rounded bg-muted" />
          </div>
        )}
        {!isLoadingInstruments && matches.slice(0, LOCAL_MATCH_LIMIT).map((instrument) => (
          <button
            key={instrument.id}
            type="button"
            className="w-full text-left px-3 py-2 hover:bg-accent"
            data-testid="instrument-option"
            onClick={() => onChange(instrument)}
          >
            <p className="text-sm font-medium">{instrument.symbol}</p>
            <p className="text-xs text-muted-foreground">
              {instrument.name}
              {instrument.isin ? ` · ${instrument.isin}` : ""}
            </p>
          </button>
        ))}
        {!isLoadingInstruments && matches.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground" aria-live="polite">
            {t(emptyInstrumentMessageKey())}
          </p>
        )}
      </div>

      {!accountScope && (
        <p className="text-xs text-muted-foreground" data-testid="instrument-no-account-scope">
          {t("portfolio.instrumentNoAccountScope")}
        </p>
      )}

      {accountScope && identifier && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={isRequesting}
          data-testid="instrument-request-identifier"
          onClick={() => requestInstrument(identifier)}
        >
          {isRequesting ? t("portfolio.instrumentResolving") : t("portfolio.requestByIdentifier")}
        </Button>
      )}

      {accountScope && !identifier && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={isSearching || isRequesting || !query.trim()}
          data-testid="instrument-search-external"
          onClick={handleSearchExternal}
        >
          {isSearching ? t("portfolio.searching") : t("portfolio.searchExternal")}
        </Button>
      )}

      {candidates.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{t("portfolio.externalResults")}</p>
            <p className="text-xs text-muted-foreground" data-testid="preview-result-count">
              {candidates.length === 1
                ? t("portfolio.previewResultCountOne")
                : t("portfolio.previewResultCount").replace("{count}", String(candidates.length))}
            </p>
          </div>
          <div
            className="max-h-40 overflow-y-auto rounded-md border border-input divide-y"
            data-testid="instrument-candidate-list"
          >
            {candidates.map((candidate) => (
              <button
                key={candidate.figi || candidate.isin}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-accent disabled:opacity-50"
                disabled={isRequesting}
                data-testid="instrument-candidate"
                onClick={() => handleRequestCandidate(candidate)}
              >
                <p className="text-sm font-medium" data-testid="preview-row-primary">
                  {[candidate.symbol, candidate.exchange ? exchangeDisplayName(candidate.exchange) : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="text-xs text-muted-foreground">{candidate.name}</p>
                <p className="text-xs text-muted-foreground" data-testid="preview-row-secondary">
                  {candidate.figi}
                </p>
              </button>
            ))}
          </div>
          {isRequesting && (
            <p className="text-xs text-muted-foreground" data-testid="instrument-resolving">
              {t("portfolio.instrumentResolving")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
